import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MessageSquare, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';
import MarketingNav from '@/components/layout/MarketingNav';
import MarketingFooter from '@/components/layout/MarketingFooter';
import Seo from '@/components/Seo';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const upd = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/support/contact', form);
      setSent(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="bg-boosta-ink min-h-screen text-white">
      <Seo title="Contact Boosta Support — We Reply Within One Business Day"
        description="Billing question, bug report or feature idea? Message the Boosta support team and get a reply straight to your inbox." />
      <MarketingNav />
      <div className="relative pt-32 pb-24 px-4 md:px-8 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-25" />
        <div className="absolute inset-0 grain" />
        <div className="relative max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan mb-4">Support</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-3d">
              Talk to a <span className="gradient-text">human</span>.
            </h1>
            <p className="mt-5 text-white/55 text-lg leading-relaxed max-w-md">
              Billing question, bug report or feature idea — send it over and we'll get back to you, usually within one business day.
            </p>
            <div className="mt-8 space-y-4 text-sm">
              <div className="flex items-center gap-3 text-white/70">
                <span className="w-9 h-9 rounded-xl glass flex items-center justify-center"><Mail className="w-4 h-4 text-boosta-yellow"/></span>
                Every message lands straight in our support inbox
              </div>
              <div className="flex items-center gap-3 text-white/70">
                <span className="w-9 h-9 rounded-xl glass flex items-center justify-center"><MessageSquare className="w-4 h-4 text-boosta-cyan"/></span>
                Include your account email for billing queries
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.6 }}
            className="glass rounded-3xl p-8">
            {sent ? (
              <div className="text-center py-12" data-testid="contact-success">
                <CheckCircle2 className="w-12 h-12 text-boosta-emerald mx-auto mb-4" />
                <div className="font-display font-bold text-2xl">Message sent.</div>
                <p className="text-white/55 text-sm mt-2">Thanks — we'll get back to you shortly at {form.email}.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40 block mb-2">Your name</label>
                  <input required value={form.name} onChange={upd('name')} data-testid="contact-name"
                    className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-boosta-cyan/60 outline-none transition-colors" placeholder="Jane Smith"/>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40 block mb-2">Email</label>
                  <input required type="email" value={form.email} onChange={upd('email')} data-testid="contact-email"
                    className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-boosta-cyan/60 outline-none transition-colors" placeholder="jane@company.com"/>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40 block mb-2">How can we help?</label>
                  <textarea required minLength={10} rows={5} value={form.message} onChange={upd('message')} data-testid="contact-message"
                    className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-boosta-cyan/60 outline-none transition-colors resize-none" placeholder="Tell us what's going on…"/>
                </div>
                <button type="submit" disabled={busy} data-testid="contact-submit"
                  className="w-full btn-primary rounded-xl py-3.5 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
                  <Send className="w-4 h-4"/> {busy ? 'Sending…' : 'Send message'}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
      <MarketingFooter />
    </div>
  );
}
